import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import MobileVendor from '../../components/MobileVendor';
import DesktopVendor from '../../components/DesktopVendor';
import { Form } from 'react-final-form';
import VendorQualificationsContext from 'providers/VendorQualification/VendorQualifications.context';
import { useTranslation } from 'react-i18next';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import utils from 'utils';
import VendorSuccess from '../../components/VendorSuccess';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './VendorForm.styles.scss';
import './VendorForm.styles.responsive.scss';

type VendorFormProps = {
  className?: string;
};

const VendorForm: React.FC<VendorFormProps> = (props) => {
  const { className } = props;

  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const { recaptchaRef } = useContext(VendorQualificationsContext);
  const { currentUser } = useContext(CurrentUserContext);
  const { popError, popServerError } = useContext(PopNotificationsContext);
  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames('vendor-form', className);

  const initialValues = useMemo(() => {
    return !currentUser
      ? {}
      : {
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
          email: currentUser?.email,
          contactNumber: currentUser?.contactNumber,
        };
  }, [currentUser]);

  const getPreparedValues = useCallback((values: any) => {
    const {
      firstName,
      lastName,
      contactNumber,
      email,
      password,
      consideredAspectsOfDeal,
      revenue,
      profit,
      netAssets,
      compensationPaidToOwner,
      ...restValues
    } = values;

    const parsedProfit = utils.getIntegerFromDottedNumber(profit);
    const parsedNetAssets = utils.getIntegerFromDottedNumber(netAssets);
    const parsedRevenue = utils.getIntegerFromDottedNumber(revenue);
    const parsedCompensationPaidToOwner = utils.getIntegerFromDottedNumber(
      compensationPaidToOwner,
    );

    const parsedConsideredAspectsOfDeal: any = [];
    Object.entries(consideredAspectsOfDeal || {}).forEach(([key, value]) => {
      if (value) parsedConsideredAspectsOfDeal.push(key);
    });

    return {
      firstName,
      lastName,
      contactNumber,
      email,
      password,
      vendorEnquiry: {
        revenue: parsedRevenue,
        netAssets: parsedNetAssets,
        profit: parsedProfit,
        compensationPaidToOwner: parsedCompensationPaidToOwner,
        consideredAspectsOfDeal: parsedConsideredAspectsOfDeal,
        ...restValues,
      },
    };
  }, []);

  const handleUserSubmit = useCallback(
    async (values: any) => {
      const preparedValues = getPreparedValues(values);

      try {
        await api.vendor.updateVendorEnquiry(preparedValues);
        setShowSuccessScreen(true);
      } catch (e) {
        popServerError(e);
      }
    },
    [getPreparedValues, popServerError],
  );

  const handleSubmit = useCallback(
    async (values: any) => {
      const recaptchaResponseToken = recaptchaRef.current?.getValue();

      if (!recaptchaResponseToken) {
        popError(t('recaptchaRequired'));
        return;
      }

      const preparedValues = getPreparedValues(values);

      try {
        await api.vendor.signup({ ...preparedValues, recaptchaResponseToken });
        setShowSuccessScreen(true);
      } catch (e) {
        popServerError(e);
        recaptchaRef.current?.reset();
      }
    },
    [getPreparedValues, popError, popServerError, recaptchaRef, t],
  );

  return (
    <div className={classes}>
      <Form
        initialValues={initialValues}
        onSubmit={currentUser ? handleUserSubmit : handleSubmit}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {showSuccessScreen ? (
              <VendorSuccess />
            ) : isPhablet ? (
              <MobileVendor />
            ) : (
              <DesktopVendor />
            )}
          </form>
        )}
      />
    </div>
  );
};

export default VendorForm;
